import React, { useState, useEffect, useRef, useCallback } from 'react';

const ContactForm = () => {
  const recaptchaRef = useRef(null);
  const formDataRef = useRef(null);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
  const loadRecaptchaScript = () => {
    const existingScript = document.querySelector('script[src="https://www.google.com/recaptcha/api.js?render=explicit"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = renderRecaptcha;
      document.body.appendChild(script);
    } else {
      renderRecaptcha();
    }
  };

  loadRecaptchaScript();
}, [recaptchaWidgetId]);

const SITE_KEY = "6LeM25crAAAAAIQO2WPaRjyHS4ULjP5FGg9TapW5";

const renderRecaptcha = () => {
  if (
    window.grecaptcha &&
    typeof window.grecaptcha.render === 'function' &&
    recaptchaRef.current &&
    recaptchaWidgetId === null
  ) {
    const widgetId = window.grecaptcha.render(recaptchaRef.current, {
      sitekey: SITE_KEY,
      size: 'normal',
      callback: () => {},
      'expired-callback': () => console.log('reCAPTCHA expired'),
      'error-callback': () => console.log('reCAPTCHA error'),
    });
    setRecaptchaWidgetId(widgetId);
    console.log('✅ reCAPTCHA rendered with ID:', widgetId);
  } else {
    setTimeout(renderRecaptcha, 300); // retry after short delay
  }
};


  const handleCaptchaCallback = useCallback(async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formDataRef.current, captchaToken: token })
      });

      if (response.ok) {
        setSuccess(true);
        setError('');
        setFormData({ name: '', email: '', phone: '', message: '' });
        if (window.grecaptcha && recaptchaWidgetId !== null) {
          window.grecaptcha.reset(recaptchaWidgetId);
        }
      } else {
        throw new Error('Server error while submitting form');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [recaptchaWidgetId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const { name, email, phone, message } = formData;
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!name.trim()) return setError('Name is required.');
    if (!email.trim() || !email.includes('@')) return setError('Valid email is required.');
    if (!phoneRegex.test(phone)) return setError('Enter a valid 10-digit phone number.');
    if (!message.trim() || message.length < 10) return setError('Message must be at least 10 characters.');

    const token = window.grecaptcha?.getResponse(recaptchaWidgetId);
    if (!token) return setError('Please complete the reCAPTCHA.');

    setIsLoading(true);
    await handleCaptchaCallback(token);
  };

  const styles = {
    container: { maxWidth: '800px', margin: '0 auto', padding: '2rem', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
    formGroup: { marginBottom: '1.5rem' },
    label: { display: 'block', marginBottom: '0.5rem' },
    input: { width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' },
    textarea: { width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' },
    button: { padding: '1rem', width: '100%', backgroundColor: 'red', color: '#fff', border: 'none', borderRadius: '4px' },
    alert: { padding: '1rem', borderRadius: '4px', marginBottom: '1rem' },
    success: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
    error: { backgroundColor: '#ffebee', color: '#c62828' },
    captchaContainer: { marginBottom: '1rem', display: 'flex', justifyContent: 'center' }
  };

  return (
    <div style={styles.container}>
      <h2>Contact Us</h2>

      {success && <div style={{ ...styles.alert, ...styles.success }}>Message sent successfully!</div>}
      {error && <div style={{ ...styles.alert, ...styles.error }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Name</label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} style={styles.input} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Email</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} style={styles.input} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Phone</label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} style={styles.input} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Message</label>
          <textarea name="message" value={formData.message} onChange={handleChange} style={styles.textarea} />
        </div>

        <div ref={recaptchaRef} style={styles.captchaContainer}></div>

        <button type="submit" disabled={isLoading} style={styles.button}>
          {isLoading ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
